import { useEffect, useState } from "react";
import { CreditCard, Save, Trash2, PlugZap, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Pannello Pagamenti Stripe (solo titolare).
 * - Mostra solo stato "configurata + ultime 4" (mai la chiave in chiaro)
 * - PUT /settings/stripe salva cifrato nel DB
 * - POST /settings/stripe/test chiama Stripe (sola lettura)
 */
export default function StripeCredentialsPanel() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [whSecret, setWhSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/settings/stripe/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ api_key: { configured: false }, webhook_secret: { configured: false } });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey && !whSecret) {
      toast.error("Inserisci almeno un valore"); return;
    }
    setSaving(true);
    try {
      await api.put("/settings/stripe", {
        api_key: apiKey || undefined,
        webhook_secret: whSecret || undefined,
      });
      toast.success("Credenziali salvate (cifrate nel database)");
      setApiKey(""); setWhSecret(""); setTestResult(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/settings/stripe/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success("Stripe risponde correttamente");
      else toast.error(r.data.error || "Stripe ha rifiutato la chiave");
    } catch (e) {
      const d = e?.response?.data?.detail || "Errore connessione";
      setTestResult({ ok: false, error: d });
      toast.error(d);
    } finally { setTesting(false); }
  };

  const clearAll = async () => {
    if (!window.confirm("Rimuovere le credenziali Stripe salvate? Il backend tornerà a usare quelle in .env (se presenti).")) return;
    try {
      await api.delete("/settings/stripe");
      toast.success("Credenziali rimosse"); load();
    } catch (e) { toast.error("Errore"); }
  };

  const Field = ({ label, cfg }) => (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-xs">
        {cfg?.configured ? (
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <CheckCircle2 size={12} />
            configurata
            {cfg.last4 && <span className="font-mono text-white/60 ml-1">…{cfg.last4}</span>}
            {cfg.source === "env" && <span className="text-amber-400/80 ml-2">(da .env)</span>}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-white/50">
            <AlertTriangle size={12} /> non configurata
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="glass rounded-2xl p-6" data-testid="stripe-credentials-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-[#ff0033]" />
          <div className="font-display font-bold uppercase tracking-wider text-sm">Pagamenti Stripe</div>
        </div>
        {status?.updated_at && (
          <div className="text-[11px] text-white/40 font-mono">
            aggiornato: {new Date(status.updated_at).toLocaleString("it-IT")}
          </div>
        )}
      </div>
      <p className="text-xs text-white/45 mb-4">
        Chiavi cifrate nel database (stesso schema AES-256-GCM del Codice Fiscale). Non vengono mai restituite in chiaro né registrate nei log.
      </p>

      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        <Field label="Chiave segreta (sk_/rk_)" cfg={status?.api_key} />
        <Field label="Segreto webhook (whsec_)" cfg={status?.webhook_secret} />
      </div>

      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40">Nuova chiave segreta</label>
          <input data-testid="stripe-api-key" type="password" autoComplete="off"
            placeholder="sk_live_… oppure rk_live_…"
            value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:border-[#ff0033]/60 focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/40">Nuovo segreto webhook</label>
          <input data-testid="stripe-webhook-secret" type="password" autoComplete="off"
            placeholder="whsec_…"
            value={whSecret} onChange={(e) => setWhSecret(e.target.value)}
            className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:border-[#ff0033]/60 focus:outline-none" />
        </div>
        <div className="flex items-center gap-2 justify-end pt-1">
          <button type="button" onClick={test} disabled={testing || !status?.api_key?.configured}
            data-testid="stripe-test-btn"
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-white/10 disabled:opacity-40">
            {testing ? <Loader2 size={12} className="animate-spin" /> : <PlugZap size={12} />}
            Prova collegamento
          </button>
          <button type="button" onClick={clearAll} disabled={!status?.api_key?.configured && !status?.webhook_secret?.configured}
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-red-500/15 hover:border-red-500/40 text-white/80 disabled:opacity-40">
            <Trash2 size={12} /> Rimuovi dal DB
          </button>
          <button type="submit" disabled={saving || (!apiKey && !whSecret)} data-testid="stripe-save-btn"
            className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-40">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Salva (cifrato)
          </button>
        </div>
      </form>

      {testResult && (
        <div className={`mt-4 rounded-lg border px-3 py-2 text-xs ${testResult.ok ? "border-emerald-400/40 bg-emerald-400/5" : "border-amber-400/40 bg-amber-400/5"}`}
          data-testid="stripe-test-result">
          {testResult.ok ? (
            <div className="text-emerald-300">
              <CheckCircle2 size={12} className="inline mr-1" />
              OK · account <span className="font-mono">{testResult.account_id}</span>
              {testResult.country && <> · <span>{testResult.country}</span></>}
              {typeof testResult.charges_enabled === "boolean" && (
                <> · {testResult.charges_enabled ? "incassi attivi" : "incassi disattivi"}</>
              )}
            </div>
          ) : (
            <div className="text-amber-300">
              <AlertTriangle size={12} className="inline mr-1" />
              {testResult.error || "Chiave non valida"}
              {testResult.status && <span className="ml-2 text-white/50">(HTTP {testResult.status})</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
